<script setup lang="ts">
import { useField, useForm } from "vee-validate";
import { toTypedSchema } from "@vee-validate/zod";
import * as zod from "zod";
import { onMounted, ref } from "vue";
import router from "@/router";
import BaseLoading from "@/components/Common/BaseLoading.vue";
import { toast } from "vue-sonner";
import { PicPayload, PicResponse } from "@/types/interface/pic";
import { usePicStore } from "@/stores/master/pic";
import { useCategoryGroupStore } from "@/stores/master/category-group-store";
import { mapOptionMultiselect } from "@/utils/helper";
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import { usePositionStore } from "@/stores/master/position";

const idPic = ref<string>("");
const isEdit = ref<boolean>(false);
const isLoading = ref<boolean>(false);
const picStore = usePicStore();
const detailPic = ref<PicResponse>();
const categoryGroupStore = useCategoryGroupStore();
const listGroupCategory = ref();
const jobStore = usePositionStore();
const listJob = ref();

const validationSchema = toTypedSchema(
  zod.object({
    job_id: zod.preprocess(
      (val) => (val && typeof val === "object" ? val.value : val),
      zod.number()
    ),
    group_category_id: zod.preprocess(
      (val) => (val && typeof val === "object" ? val.value : val),
      zod.number()
    ),
  })
);

const { handleSubmit, errors } = useForm({
  validationSchema,
});

const { value: job_id } = useField<any>("job_id");
const { value: group_category_id } = useField<any>("group_category_id");

const onSubmit = handleSubmit(async (values) => {
  isLoading.value = true;
  const payloadPic: PicPayload = {
    job_id: job_id.value?.value,
    group_category_id: group_category_id.value?.value,
  };

  const responseApi = ref<any>();

  if (isEdit.value == true) {
    responseApi.value = await picStore.dispatchUpdate(idPic.value, payloadPic);
  } else {
    responseApi.value = await picStore.dispatchCreate(payloadPic);
  }

  const { success, message } = responseApi.value;
  if (success == true) {
    setTimeout(() => {
      isLoading.value = false;
      toast.success(
        isEdit.value ? "PIC Successfully Updated" : "PIC Successfully Added"
      );
      router.push("/data/pic");
    }, 1000);
  } else {
    isLoading.value = false;
    toast.error(message);
  }
});

const loadDetailPic = async () => {
  const { success, message } = await picStore.dispatchDetail(idPic.value);
  if (success) {
    detailPic.value = await picStore?.pic?.data;
    job_id.value = mapOptionMultiselect(detailPic.value?.job, "id", "name");
    group_category_id.value = mapOptionMultiselect(
      detailPic.value?.groupCategory,
      "id",
      "code",
      "name"
    );
  }
};

const loadListGroupCategory = async () => {
  const { success, message } =
    await categoryGroupStore.dispatchGetCategoryGroups(1, 99);
  if (success) {
    listGroupCategory.value = mapOptionMultiselect(
      await categoryGroupStore.categoryGroups.items,
      "id",
      "code",
      "name"
    );
  }
};

const loadListJob = async () => {
  const { success, message } = await jobStore.dispatchGetPositions(1, 99);
  if (success) {
    listJob.value = mapOptionMultiselect(
      await jobStore.positions.items,
      "id",
      "code",
      "name"
    );
  }
};

loadListGroupCategory();
loadListJob();

onMounted(() => {
  if (router.currentRoute.value.params.id != undefined) {
    isEdit.value = true;
    idPic.value = router.currentRoute.value.params.id.toString();
    loadDetailPic();
  }
});
</script>
<template>
  <div class="row justify-content-center">
    <div class="col-lg-12">
      <div class="card bg-white border-0 rounded-3 mb-4">
        <div class="card-body p-4">
          <div
            class="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-3 mb-lg-4"
          >
            <h3 class="mb-0">Form Create PIC</h3>
            <a
              class="btn btn-danger fw-semibold text-white px-3"
              type="button"
              href="/data/pic"
            >
              <i class="ri-arrow-drop-left-line text-gray-light"></i>
              Back To List PIC
            </a>
          </div>
          <BaseLoading text="Processing Data..." v-if="isLoading" />
          <form
            @submit="onSubmit"
            class="row g-3 needs-validation form"
            v-if="!isLoading"
          >
            <div class="row">
              <div class="col-lg-6">
                <div class="form-group">
                  <label for="job_id" class="form-label label text-secondary"
                    >Designation <span class="text-danger">*</span></label
                  >
                  <div class="position-relative">
                    <vSelect
                      :options="listJob"
                      name="job_id"
                      v-model="job_id"
                      placeholder="Choose Designation"
                    ></vSelect>
                  </div>
                  <span class="text-danger">{{ errors.job_id }}</span>
                </div>
              </div>
              <div class="col-lg-6">
                <div class="form-group">
                  <label
                    for="group_category_id"
                    class="form-label label text-secondary"
                    >Group Category <span class="text-danger">*</span></label
                  >
                  <div class="position-relative">
                    <vSelect
                      :options="listGroupCategory"
                      name="group_category_id"
                      v-model="group_category_id"
                      placeholder="Group Category"
                    ></vSelect>
                  </div>
                  <span class="text-danger">{{
                    errors.group_category_id
                  }}</span>
                </div>
              </div>
              <div class="col-12 mt-5">
                <button
                  class="btn btn-primary fw-semibold text-white py-2 px-3"
                  type="submit"
                >
                  Save
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>
